import { useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import {
  ErrorView,
  Loader,
  PercentButton,
  FieldInput,
  UpdateModalInformation
} from '@percent/admin-dashboard/common/components'
import { ReactComponent as CloseIcon } from '@percent/admin-dashboard/common/assets/images/close.svg'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { PatchOrganisationParams } from '@percent/admin-dashboard/api/types'
import styles from './UpdateEmailModal.module.scss'
import { UpdateEmailModalProps } from './UpdateEmailModal.types'
import { ReactComponent as AlertIcon } from '@percent/admin-dashboard/common/assets/images/alert.svg'
import { emailRegex, useMutation } from '@percent/utility'
import { Divider, Modal, Card } from '@percent/lemonade'

export function UpdateEmailModal({
  organisation,
  refresh,
  agentVerificationId,
  isModalOpened,
  setModalOpened
}: UpdateEmailModalProps) {
  const { t } = useTranslation()
  const [isSent, setIsSent] = useState(false)
  const { adminService } = useServices()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { values, setFieldValue, handleBlur, touched, errors, dirty, isValid } = useFormik({
    initialValues: {
      email: organisation?.email
    },
    validationSchema: () =>
      yup.object().shape({
        email: yup.string().required('Required').matches(emailRegex, t('errorMessage.validEmail')).max(255).nullable()
      }),
    onSubmit: () => {}
  })

  const [{ isPending, errorMessage }, { apiFunc: aproveFunc }] = useMutation(adminService.editOrganisation, () => {
    setIsSent(true)
    queryClient.invalidateQueries({ queryKey: ['/organisations', organisation?.id] })
  })

  const handleUpdate = () => {
    aproveFunc({ id: organisation?.id, payload: { email: values.email } } as PatchOrganisationParams)
  }

  const handleCloseModalAfterUpdate = () => {
    refresh()
    setIsSent(false)
    navigate(`/agent-verification/${agentVerificationId}`)
    setModalOpened(false)
  }

  return (
    <Modal width={600} open={isModalOpened} onClose={() => setModalOpened(false)}>
      <div className={styles.modal}>
        {isSent ? (
          <UpdateModalInformation
            handleClick={handleCloseModalAfterUpdate}
            setModalOpened={setModalOpened}
            title={t('dialog.updateEmail.emailUpdated')}
            subtitle={<p>{t('dialog.updateEmail.emailStored')}</p>}
            buttonTitle={t('button.goBackToDetails')}
          />
        ) : (
          <Card className={styles.wrapper}>
            <div className={styles.titleWrapper}>
              <span className={styles.title}>
                {t(`dialog.updateEmail.${organisation?.email ? 'editEmail' : 'addEmail'}`)}
              </span>
              <CloseIcon onClick={() => setModalOpened(false)} />
            </div>
            <Divider className={styles.divider} />
            <div className={styles.textWrapper}>{t('dialog.updateEmail.body')}</div>
            <div className={styles.alertWrapper}>
              <span>
                <AlertIcon className={styles.alertIcon} />
              </span>
              <span>{t('dialog.updateWebsite.bodyMore')}</span>
            </div>
            <FieldInput
              name="email"
              label={t('form.nonprofitGeneralEmail')}
              placeHolder={t('form.email')}
              onChange={(value: string) => setFieldValue('email', value)}
              value={values.email}
              handleBlur={handleBlur}
              error={errors.email}
              touched={touched.email}
            />
            {errorMessage && <ErrorView errorMessage={errorMessage} />}
            {isPending ? (
              <Loader />
            ) : (
              <div className={styles.buttonWrapper}>
                <PercentButton
                  testId="submit-email-button"
                  title={t('button.submit')}
                  onClick={handleUpdate}
                  disabled={!(isValid && dirty)}
                />
                <PercentButton
                  testId="cancel-email-button"
                  title={t('button.cancel')}
                  onClick={() => setModalOpened(false)}
                  textColour="secondaryTextColour"
                  outlined
                />
              </div>
            )}
          </Card>
        )}
      </div>
    </Modal>
  )
}
